import {
  Box,
  Disclaimer,
  Div,
  H5,
  P,
  Pill,
  StatusMark,
  centsToDollars,
  formatter,
  lightTheme,
} from '@vgrubs/components';
import {
  AgentIncomeStatus,
  AgentIncomeType,
  AgentTransactionsType,
  NewTransactionItem,
} from 'components/src/types/api/wallet.types';
import { DateTime } from 'luxon';
import {
  IconBalance,
  IconIncome,
  IconWithdrawal,
  amountColorByValue,
} from './TransactonsStyled';

// ---------------------------------------------------------------------------
// Status badge + row description helpers
// ---------------------------------------------------------------------------

export const showAgentStatusColor = (status: AgentIncomeStatus | undefined) => {
  switch (status) {
    case AgentIncomeStatus.active:
      return lightTheme.yellow;
    case AgentIncomeStatus.pending:
      return lightTheme.yellow;
    case AgentIncomeStatus.available:
      return lightTheme.green;
    default:
      return lightTheme.lightGray;
  }
};

// Row titles follow the modal's Description field, minus the trailing
// "Commission" - the list stays short and the id carries the detail.
const describeAgentRowIncome = (type: AgentIncomeType) => {
  switch (type) {
    case AgentIncomeType.vGrubs:
      return 'Income - Order';
    case AgentIncomeType.vDrive:
      return 'Income - vDrive';
    default:
      return describeAgentIncome(type);
  }
};

// Appends the order/delivery id to a row title when the income carries one.
const withTransactionId = (title: string, id?: number | null) =>
  id ? `${title} - #${id}` : title;

// Per-row visual: title text + amount sign + amount colour
export const describeAgentRow = (
  obj: NewTransactionItem['object'],
  txType: AgentTransactionsType
) => {
  if (txType === AgentTransactionsType.withdrawals) {
    return { title: 'Withdrawal', sign: '-', amountColor: undefined };
  }

  switch (obj.type) {
    case AgentIncomeType.vGrubs:
    case AgentIncomeType.vOrders:
    case AgentIncomeType.vDrive:
      return {
        // Title mirrors the Description field of the detail modal, followed by
        // the order id - always the order id, never the delivery one, so the
        // list reads consistently. Incomes without an order id keep the plain
        // label.
        title: withTransactionId(describeAgentRowIncome(obj.type), obj?.orderId),
        sign: obj?.amount && obj?.amount > 0 ? '+' : '',
        amountColor: amountColorByValue(obj?.amount),
      };
    case AgentIncomeType.flatAmount:
      return {
        title: 'Flat Amount',
        sign: '+',
        amountColor: obj?.amount ? lightTheme.green : lightTheme.darkGray,
      };
    case AgentIncomeType.pipeline:
      return {
        title: 'Pipeline Earning',
        sign: '+',
        amountColor: obj?.amount ? lightTheme.green : lightTheme.darkGray,
      };
    case AgentIncomeType.balanceIncrementation:
      return {
        title: 'Balance Correction',
        sign: '+',
        amountColor: obj?.amount ? lightTheme.green : lightTheme.darkGray,
      };
    case AgentIncomeType.balanceDeduction:
      return {
        title: 'Balance Correction',
        sign: '-',
        amountColor: obj?.amount ? lightTheme.primary : lightTheme.darkGray,
      };
    default:
      return { title: 'Income', sign: '+', amountColor: lightTheme.green };
  }
};

export const describeAgentIncome = (type: AgentIncomeType | undefined) => {
  switch (type) {
    case AgentIncomeType.vGrubs:
      return 'Income - Order Commission';
    case AgentIncomeType.vOrders:
      return 'Income - Monthly SaaS';
    case AgentIncomeType.vDrive:
      return 'Income - vDrive Commission';
    case AgentIncomeType.flatAmount:
      return 'Income - Flat Commission';
    case AgentIncomeType.pipeline:
      return 'Income - Commission from Agent';
    case AgentIncomeType.balanceIncrementation:
    case AgentIncomeType.balanceDeduction:
      return 'Balance Correction';
    default:
      return 'Income';
  }
};

// ---------------------------------------------------------------------------
// Row component — one entry in the grouped-by-date list
// ---------------------------------------------------------------------------

export const AgentTransactionRow = ({
  item,
  txType,
  onClick,
}: {
  item: NewTransactionItem;
  txType: AgentTransactionsType;
  onClick: () => void;
}) => {
  const obj = item.object;
  const { title, sign, amountColor } = describeAgentRow(obj, txType);

  return (
    <Box
      $relative
      $flex
      $alignCenter
      $between
      $mb="8"
      $mbSM="8"
      $pointer
      onClick={onClick}
    >
      <StatusMark $color={showAgentStatusColor(obj.status)} />
      <Div $flex $alignCenter $gap="20px" $gapSM="16px">
        {txType === AgentTransactionsType.incomes ? (
          item.object.type === AgentIncomeType.balanceDeduction ? (
            <IconBalance />
          ) : (
            <IconIncome />
          )
        ) : (
          <IconWithdrawal />
        )}
        <Div $width="100%">
          <P $m="0">{title}</P>
          <Disclaimer $color={lightTheme.lightGray} $mb="0">
            {DateTime.fromISO(item.createdAt).toFormat('hh:mm a')}
          </Disclaimer>
        </Div>
      </Div>
      <Div $flex $alignCenter $gap="12px">
        <H5 $right $mb="0" $color={amountColor}>
          {sign}
          {formatter.format(centsToDollars(item.amount))}
        </H5>
      </Div>
    </Box>
  );
};
